import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { tierOfGroups } from '../../agents/billingApi.ts';

/**
 * After Stripe checkout redirects to `/app/account?billing=success`, the
 * webhook has assigned the tier group in Cognito but the browser's access
 * token still carries the pre-checkout groups — so the page reads "Free".
 * Force-refresh the token (Cognito stamps the CURRENT groups into a fresh
 * one) and poll to cover the webhook landing a beat after the redirect.
 * Returns a "confirming…" banner while it runs (or null).
 */
export function useBillingReturn(refresh: () => Promise<boolean>): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirming, setConfirming] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (searchParams.get('billing') !== 'success') {
      return;
    }
    cancelledRef.current = false;
    setConfirming(true);
    void (async () => {
      // Loop condition guards each iteration; the end guard handles unmount
      // during the final refresh — no mid-loop check needed.
      for (let attempt = 0; attempt < 6 && !cancelledRef.current; attempt++) {
        try {
          const session = await fetchAuthSession({ forceRefresh: true });
          const claim: unknown = session.tokens?.accessToken.payload['cognito:groups'];
          const list = Array.isArray(claim) ? claim.map(String) : [];
          await refresh();
          if (tierOfGroups(list) !== null) {
            break; // the paid tier is now in the token
          }
        } catch {
          // transient — retry
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelledRef.current) {
        setConfirming(false);
        searchParams.delete('billing');
        setSearchParams(searchParams, { replace: true });
      }
    })();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!confirming) {
    return null;
  }
  return (
    <div className="om-account__billing-confirm" role="status">
      Confirming your subscription…
    </div>
  );
}
